import type { Placement } from 'react-joyride';
import { DemoPersonaRole } from '../services/demoService';

/**
 * The guided tour, as data.
 *
 * A short highlight reel rather than a full walkthrough: one memorable thing per persona, opening
 * on the student (the part people react to) and closing on what an administrator is actually
 * buying. A visitor who wants more can explore freely afterwards with the persona bar.
 *
 * `target` is optional. When it is absent — or the element is not on the page — the step is shown
 * centred instead of anchored. That matters more than it sounds: a sales demo that throws up a
 * broken tooltip because a page changed is worse than one that simply narrates the screen. Every
 * step below does set one, anchored to a `data-demo-id` attribute (never a MUI-generated class or
 * DOM structure, which breaks silently on refactor) — but the fallback stays load-bearing.
 */
export interface DemoTourStep {
  /** Which persona this step is seen as. The tour switches automatically when it changes. */
  persona: DemoPersonaRole;
  /** Where to navigate before showing the step. */
  route: string;
  /** CSS selector to spotlight. Falls back to a centred step when absent or not found. */
  target?: string;
  /** Overrides the default ('auto' when targeted, 'center' otherwise) for a step where automatic
   * placement picks badly — e.g. a target near a screen edge with little room on its preferred side. */
  placement?: Placement | 'auto';
  title: string;
  body: string;
}

export const DEMO_TOUR: DemoTourStep[] = [
  {
    persona: 'student',
    route: '/dashboard',
    target: '[data-demo-id="student-welcome"]',
    title: 'This is what a student sees',
    body:
      'You are signed in as Daniel, a 7th grader at Northgate. Everything here is a working copy '
      + 'of the platform — your own copy, filled with a term of real activity. Change anything you '
      + 'like; nobody else sees it.',
  },
  {
    persona: 'student',
    route: '/dashboard',
    target: '[data-demo-id="student-stats-card"]',
    title: 'Progress they can see',
    body:
      'Points, accuracy, rank and how far they are from the next one. Students check this the way '
      + 'they check a game — which is the point.',
  },
  {
    persona: 'student',
    route: '/student/quizzes',
    target: '[data-demo-id="quizzes-list"]',
    title: 'Try a quiz yourself',
    body:
      'Open one. Questions are timed, answers are graded instantly, and the score reveal is '
      + 'deliberately theatrical. This is the part students ask to do again.',
  },
  {
    persona: 'student',
    route: '/student/achievements',
    target: '[data-demo-id="achievements-grid"]',
    title: 'Badges do the nagging for you',
    body:
      'Earned badges, badges in progress, and how rare each one is. The locked ones are the point: '
      + 'they tell a student exactly what to go and practise.',
  },
  {
    persona: 'student',
    // Points, not Ranked — the copy is about session/team scoping, which is the Points tab's
    // "View Leaderboard" selector. Ranked (the page's own default) has no such control.
    route: '/leaderboard?tab=points',
    target: '[data-demo-id="leaderboard-scope-select"]',
    title: 'Competition, scoped sensibly',
    body:
      'League-wide, by session, or by team. Teams matter here — a student who will not compete for '
      + 'themselves will happily compete for their table.',
  },
  {
    persona: 'teacher',
    route: '/teacher/dashboard',
    target: '[data-demo-id="teacher-roster-card"]',
    title: 'Now you are the coach',
    body:
      'Same data, a completely different job. This is James Rivera\'s roster — seven students he '
      + 'invited with a single shareable link. No account admin, no spreadsheets.',
  },
  {
    persona: 'teacher',
    route: '/teacher/analytics',
    target: '[data-demo-id="teacher-struggling-students"]',
    title: 'Who is quietly falling behind',
    body:
      'The question every coach actually has. Class performance, per-quiz breakdowns, and students '
      + 'flagged as struggling before it shows up in a grade.',
  },
  {
    persona: 'parent',
    route: '/parent/dashboard',
    target: '[data-demo-id="parent-children-card"]',
    title: 'What parents get',
    body:
      'Priya has three children in the league. One login, all three, no email digest to ignore. '
      + 'Parents seeing progress is what keeps students enrolled next term.',
  },
  {
    persona: 'admin',
    route: '/admin',
    target: '[data-demo-id="admin-stats-grid"]',
    title: 'And now the part you would run',
    body:
      'Rosa administers Northgate. Sign-ups, activity and revenue at a glance, with the detail a '
      + 'click away.',
  },
  {
    persona: 'admin',
    route: '/admin/sessions',
    target: '[data-demo-id="admin-sessions-list"]',
    title: 'Building a session',
    body:
      'Drag problems into groups, reorder them, pull from a shared problem bank. Setting up a term '
      + 'is an afternoon, not a project.',
  },
  {
    persona: 'admin',
    route: '/admin/reports',
    target: '[data-demo-id="admin-reports-panel"]',
    title: 'Evidence for the people who ask',
    body:
      'Session performance, top students, problem-level difficulty, and comparisons between '
      + 'sessions — exported to CSV when a principal or a parent body wants the numbers.',
  },
  {
    persona: 'admin',
    route: '/admin/pricing',
    target: '[data-demo-id="admin-pricing-tabs"]',
    title: 'Enrolment pays for itself',
    body:
      'Set session pricing, bundle discounts for families, and promotion codes. Parents enrol and '
      + 'pay online; you are not chasing cheques.',
  },
];

/** Shown when the tour finishes, anchored on the persona bar. */
export const DEMO_TOUR_FINALE = {
  target: '[data-demo-id="persona-student"]',
  title: 'That is the tour — now go anywhere',
  body:
    'Switch between the four views any time with these tabs. Nothing you do here affects anyone '
    + 'else, and Restart puts it all back.',
};
